import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Eye, Users, Calendar, TrendingUp, Globe, ExternalLink, Copy, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Compact per-user visit stats widget.
 * Fetched from `GET /api/admin/visits/by-slug` — backend restricts a
 * user_admin token to their own slug automatically.
 */
const UserAnalyticsWidget = ({ token, slug, username, isPublic }) => {
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) return;
    axios
      .get(`${API}/admin/visits/by-slug`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (cancelled) return;
        const row = (res.data.items || []).find((r) => r.slug === slug);
        setStats(row || { total: 0, today: 0, last_7_days: 0, last_30_days: 0, unique_ips_30d: 0 });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token, slug]);

  const publicUrl = `${window.location.origin}/u/${slug}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      style={{
        marginBottom: 18,
        padding: '14px 16px',
        background: 'linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(168,85,247,0.06) 100%)',
        border: '1px solid rgba(59,130,246,0.2)',
        borderRadius: 12,
        display: 'flex',
        gap: 14,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
      data-testid="user-analytics-widget"
    >
      <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 200px', minWidth: 200 }}>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
          Xoş gəldiniz, <b style={{ color: '#fbbf24' }}>{username}</b>
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <a
            href={isPublic ? publicUrl : undefined}
            target="_blank"
            rel="noreferrer"
            style={{
              fontSize: 12,
              fontFamily: 'monospace',
              color: isPublic ? '#93c5fd' : 'rgba(255,255,255,0.35)',
              textDecoration: isPublic ? 'underline' : 'line-through',
              display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            <Globe size={12} />
            /u/{slug}
            {isPublic && <ExternalLink size={11} />}
          </a>
          <button
            onClick={copyLink}
            title="Linki kopyala"
            style={{
              background: 'rgba(59,130,246,0.15)',
              border: '1px solid rgba(59,130,246,0.35)',
              color: '#93c5fd',
              padding: '3px 8px',
              borderRadius: 6,
              fontSize: 11,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            {copied ? <Check size={11} /> : <Copy size={11} />}
            {copied ? 'Kopyalandı' : 'Linki kopyala'}
          </button>
          {!isPublic && (
            <span style={{ fontSize: 10, padding: '2px 8px', background: 'rgba(220,38,38,0.15)', color: '#fca5a5', borderRadius: 4 }}>
              Public deyil — ziyarətçilər görə bilməz
            </span>
          )}
        </div>
      </div>

      {stats && (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <Stat icon={<Eye size={14} />} label="Ümumi" value={stats.total} />
          <Stat icon={<Calendar size={14} />} label="Bu gün" value={stats.today} />
          <Stat icon={<TrendingUp size={14} />} label="Son 7 gün" value={stats.last_7_days} />
          <Stat icon={<TrendingUp size={14} />} label="Son 30 gün" value={stats.last_30_days} />
          <Stat icon={<Users size={14} />} label="Unikal ziyarətçi" value={stats.unique_ips_30d} />
        </div>
      )}
    </div>
  );
};

const Stat = ({ icon, label, value }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 62 }}>
    <div style={{ color: '#93c5fd', display: 'flex', gap: 4, alignItems: 'center', fontSize: 18, fontWeight: 800 }}>
      {icon} {value}
    </div>
    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)' }}>{label}</span>
  </div>
);

export default UserAnalyticsWidget;
